import { h } from 'preact';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import Panel from 'utils/panel';
import TrapFocus from 'utils/trap-focus';
import { formatCartLinesAjax, formatMoney } from 'utils/storefront/format-resource';
import { Close } from 'utils/icon/icons';
import { trackMiniCartATB, trackMiniCartRemove } from 'utils/tracking';
import { withCartContext } from './cart-context';
import CartItem from './cart-item';
import Upsells from './upsells';
import FreeShipping from './cart-shipping';
import updateURL from '../utils/update-url';
import Layout from '../utils/layout';
import MultiGwpCta from './multi-gwp-cta';

const Cart = ({
  cart,
  nostoTitle,
  emptyButtonText,
  emptyButtonLink,
  maxOrderQTY,
  upSellProducts,
  shUpTitle,
  freeShippingThreshold,
  freeShippingIconThresholdMet,
  freeShippingIconThresholdUnfulfilled,
  freeShippingMessageThresholdMet,
  freeShippingMessageThresholdUnfulfilled,
  multiGwpCtaThreshold,
  multiGwpCtaQuantity,
  multiGwpProducts,
  multiGwpCtaGiftImg,
  multiGwpCtaDescription,
  multiGwpCtaBtnText,
  multiGwpPanelHeading,
  multiGwpAppliedCartMsg,
  promosWithThresholds = [],
  customerMarket,
  timerId,
  timerEnabled,
}) => {
  const lines = formatCartLinesAjax(cart?.state?.items);

  const multiGwpIsReady = (multiGwpProducts.length > 0 && multiGwpCtaThreshold > 0) || false;
  const cartTotal = cart.state.total_price / 100;

  let cartTotalMinusGiftCards = 0;

  lines.forEach((item) => {
    if (!item.handle.includes('gift-card')) {
      cartTotalMinusGiftCards += item.final_price * item.quantity;
    }
  });

  const totalGCCard = cartTotalMinusGiftCards / 100;

  const closeTriggerEl = useRef(null);
  const isCartRedirect = new URLSearchParams(window.location.search).get('cart');
  const [panelTriggers, setPanelTriggers] = useState({
    openTriggerEl: document.querySelector('[data-header-cart-trigger]'),
    closeTriggerEl: null,
  });
  const [isOpen, setIsOpen] = useState(isCartRedirect);
  const layout = Layout.getInstance();
  const [isMobile, setIsMobile] = useState(); // based on screen width

  const isGwpProductOrInMultiGwp = (line) => {
    const multiGwpIds = multiGwpProducts.map((product) => product.id);
    return line.properties?._is_gwp_product === true || multiGwpIds.includes(line.product_id);
  };

  const multiGwpInCart = lines.filter(isGwpProductOrInMultiGwp);

  // eslint-disable-next-line max-len
  const getMultiGwpQty = () => multiGwpInCart.reduce((acc, line) => acc + line.quantity, 0);
  const gwpInCart = getMultiGwpQty();
  const gwpUsedCount = gwpInCart || 0;

  const [multiGwpGiftCountUsed, setMultiGwpGiftCountUsed] = useState();
  setMultiGwpGiftCountUsed(gwpUsedCount);

  // eslint-disable-next-line max-len
  const qualifiedGWP = Math.floor(totalGCCard / multiGwpCtaThreshold) * Math.floor(multiGwpCtaQuantity);
  const multiGwpGiftCountQualified = useMemo(() => qualifiedGWP, [totalGCCard]);

  useEffect(() => {
    const manageGWPCart = (cloneMultiGwpInCart, cloneMultiGWPGiftCountUsed, cloneMultiGWPGiftCountQualified) => {
      let excess = cloneMultiGWPGiftCountUsed - cloneMultiGWPGiftCountQualified;

      for (let i = 0; i < cloneMultiGwpInCart.length; i += 1) {
        if (!gwpUsedCount || excess <= 0) break;
        const item = cloneMultiGwpInCart[i];

        if (item.quantity <= excess) {
          excess -= item.quantity;
          cart.remove([item.key]);
          trackMiniCartRemove({
            item: item.title,
            itemId: item.sku,
            quantity: item.quantity,
          });
        } else if (item.quantity > excess) {
          const quantity = item.quantity - excess;
          excess = 0;

          const gwpQtyControlPayload = [
            {
              id: item.key,
              quantity,
            },
          ];
          cart.update(gwpQtyControlPayload, true);
        }
      }
    };

    manageGWPCart(multiGwpInCart, multiGwpGiftCountUsed, multiGwpGiftCountQualified);
  }, [cart.state]);

  let trapFocus = null;

  useEffect(() => {
    if (layout.isBelowBreakpoint('lg')) {
      setIsMobile(true);
    } else {
      setIsMobile(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      trapFocus = new TrapFocus(document.querySelector('[data-minicart]'));
      document.body.classList.add('no-scroll');
      document.body.classList.add('cart-open');
      trapFocus.init();
    }

    return () => {
      document.body.classList.remove('no-scroll');
      document.body.classList.remove('cart-open');
      if (trapFocus) trapFocus.destroy(document.querySelector('[data-logo]'));
    };
  }, [isOpen, trapFocus]);

  useEffect(() => {
    if (!closeTriggerEl.current) return;
    setPanelTriggers((value) => ({
      ...value,
      closeTriggerEl: closeTriggerEl.current,
    }));
  }, [closeTriggerEl]);

  const handleCheckoutClick = () => {
    if (!lines?.length) {
      window.location = updateURL('/checkout');
    }

    const items = lines?.map((line) => (line.merchandise ? line.merchandise.product.title : line.title))?.join(', ');
    const skus = lines?.map((line) => (line.merchandise ? line.merchandise.product.sku : line.sku))?.join(', ');

    trackMiniCartATB({
      item: items,
      itemId: skus,
      quantity: cart?.state?.item_count,
    });

    // Store the current page path in localStorage before navigating to checkout
    localStorage.setItem('preCheckoutPage', window.location.pathname);

    window.location = updateURL('/checkout');
  };

  const cartCount = useMemo(() => cart.state?.items?.reduce((acc, item) => acc + item.quantity, 0) || 0, [cart.state]);

  return (
    <Panel
      triggers={panelTriggers}
      defaultOpen={isCartRedirect}
      showBackdrop={true}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      context="cart"
      additionalClasses="!max-w-[538px]"
    >
      <div className="flex flex-col h-full" data-minicart>
        <div className="flex justify-between py-4 px-5 border-b border-grey-4 text-headline-tag">
          <div>
            {window.TMW.Strings.cart.title}
            {cartCount > 0 && <span> ({cartCount})</span>}
          </div>
          <button type="button" ref={closeTriggerEl} aria-label={window.TMW.Strings.cart.close}>
            <Close />
          </button>
        </div>
        <div className="cart-body h-auto overflow-y-auto grow mr-2.5 my-5">
          {timerEnabled && <div className={`gta-block__root mx-5 ${timerId}`}></div>}
          {!!upSellProducts?.length && (
            <div className="pl-5">
              {shUpTitle && <div className="text-headline-tag mb-5">{shUpTitle} </div>}
              <Upsells upsellPromos={upSellProducts} title={shUpTitle} />
            </div>
          )}
          {(promosWithThresholds.length > 0 || freeShippingThreshold > 0) && (
            <FreeShipping
              freeShippingThreshold={freeShippingThreshold}
              freeShippingIconThresholdMet={freeShippingIconThresholdMet}
              freeShippingIconThresholdUnfulfilled={freeShippingIconThresholdUnfulfilled}
              freeShippingMessageThresholdMet={freeShippingMessageThresholdMet}
              freeShippingMessageThresholdUnfulfilled={freeShippingMessageThresholdUnfulfilled}
              cart={cart}
              promosWithThresholds={promosWithThresholds}
              customerMarket={customerMarket}
            />
          )}
          {multiGwpIsReady &&
            cartTotal >= multiGwpCtaThreshold &&
            multiGwpGiftCountUsed < multiGwpGiftCountQualified && (
              <MultiGwpCta
                multiGwpCtaGiftImg={multiGwpCtaGiftImg}
                multiGwpCtaDescription={multiGwpCtaDescription}
                multiGwpCtaBtnText={multiGwpCtaBtnText}
                multiGwpPanelHeading={multiGwpPanelHeading}
                multiGwpProducts={multiGwpProducts}
                multiGwpGiftCountQualified={multiGwpGiftCountQualified}
                multiGwpGiftCountUsed={multiGwpGiftCountUsed}
                setMultiGwpGiftCountUsed={setMultiGwpGiftCountUsed}
                multiGwpInCart={multiGwpInCart}
              />
            )}

          {!!cart.errors?.length && (
            <div className="p-5 pt-8 flex flex-col gap-2">
              {cart.errors.map((error) => (
                <div key={error} className="text-body-2 text-red">{error}</div>
              ))}
            </div>
          )}
          {cart?.state?.item_count === 0 && (
            <div className="pt-5 pb-6 mx-5 border-t border-grey-4">
              <p className="text-headline-tag mb-3.5">{window.TMW.Strings.cart.empty}</p>
              {emptyButtonText && emptyButtonLink && (
                <a className="button button--primary min-w-[275px]" href={emptyButtonLink}>
                  {emptyButtonText}
                </a>
              )}
            </div>
          )}
          <div className="mx-5 border-b border-grey-4">
            {lines?.map((line) => (
              <CartItem
                key={line.id}
                line={line}
                maxOrderQTY={maxOrderQTY}
                isMobile={isMobile}
                setMultiGwpGiftCountUsed={setMultiGwpGiftCountUsed}
                multiGwpAppliedCartMsg={multiGwpAppliedCartMsg}
                getMultiGwpQty={getMultiGwpQty}
                qualifiedGWP={qualifiedGWP}
              />
            ))}
          </div>
          <div className="ml-5 py-5 border-b border-grey-4 empty:hidden" data-nosto-cart-2>
            {nostoTitle && <div className="text-headline-tag mb-5 hidden">{nostoTitle} </div>}
          </div>
          {cart?.state?.item_count > 0 && (
            <div className="mx-5 py-5 border-grey-4">
              <div className="flex justify-between text-body-2  mb-2.5">
                {window.TMW.Strings.cart.subtotal}
                <span>
                  {formatMoney({
                    amount: cart?.state?.items_subtotal_price ? cart.state.items_subtotal_price / 100 : 0,
                    currencyCode: window.TMW?.localization?.currencyCode,
                  })}
                </span>
              </div>
              <div className="flex justify-between text-body-2  mb-2.5">
                {window.TMW.Strings.cart.shipping}
                <span>
                  {formatMoney({
                    amount: cart?.state?.total_price ? cart.state.total_price / 100 : 0,
                    currencyCode: window.TMW?.localization?.currencyCode,
                  }).split('$')[1] >= freeShippingThreshold
                    ? window.TMW.Strings.cart.free
                    : window.TMW.Strings.cart.calcalatedAtCheckout}
                </span>
              </div>
              <div className="flex justify-between text-body-2  mb-2.5">
                {window.TMW.Strings.cart.taxes}
                <span>{window.TMW.Strings.cart.calcalatedAtCheckout}</span>
              </div>
            </div>
          )}
        </div>
        <div className="px-5 py-4 bg-pantone-7 border-t border-black">
          <div className="flex justify-between text-headline-tag mb-2.5">
            {window.TMW.Strings.cart.total}
            <span>
              {formatMoney({
                amount: cart?.state?.total_price ? cart.state.total_price / 100 : 0,
                currencyCode: window.TMW?.localization?.currencyCode,
              })}
            </span>
          </div>
          <button className="justify-center w-full button button--primary" onClick={handleCheckoutClick}>
            {window.TMW.Strings.cart.checkout}
          </button>
        </div>
        <button className="sr-only hidden" />
      </div>
    </Panel>
  );
};

export default withCartContext(Cart);
